import { testIds } from '@/components/base/PartnerScoreLayout/PartnerScoreLayout';
import {
  useGetPartnerScoreNavigation,
  usePartnerScoreHeader,
} from '@/components/base/PartnerScoreLayout/PartnerScoreNavigationHelpers';
import { AdobeAnalyticsConst } from '@/hooks/AdobeAnalyticsHooks/adobeAnalyticsConstants/adobeAnalyticsConstants';
import PartnerScoreContainer from '@/pages/PartnerScore/components/PartnerScoreContainer/PartnerScoreContainer';
import { getChannelTabScore } from '@/pages/PartnerScore/components/TabSwitcher/utils/helpers';
import { PartnerScoreChannel } from '@/pages/PartnerScore/components/TabSwitcher/utils/types';
import NavigationTabList, {
  type NavigationTab,
  type NavigationTabListProps,
} from '@/storybook/components/NavigationTabList/NavigationTabList';
import { AppRoutes, usePartnerScoreTabIdFromPathname } from '@/utils';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type PartnerScoreNavigationProps = {
  year: number;
  quarter: number;
  surveyIdList: Array<string>;
  children: ReactNode;
};

const NAVIGATION_TAB_ADOBE_ANALYTICS_CONFIG: NavigationTabListProps['adobeTrackingAnalytics'] = {
  trackingPosition: AdobeAnalyticsConst.POSITIONS.NAVIGATION_TABS,
  trackingPrefixName: AdobeAnalyticsConst.LINKCLICK_NAMES.PARTNER_SCORE_NAVIGATION_TABS,
};

export default function PartnerScoreNavigation({ year, quarter, surveyIdList, children }: PartnerScoreNavigationProps) {
  const { t } = useTranslation();

  const { overview, isOverviewLoading, csat, isCsatLoading, effortScore, isEffortScoreLoading, tnps, isTnpsLoading } =
    useGetPartnerScoreNavigation(year, quarter, surveyIdList);

  const navigationTabList: Array<NavigationTab> = useMemo(
    () => [
      {
        'id': PartnerScoreChannel.PARTNERSCORE,
        'label': t('main.hcpOverview.overview.navigation.overview.label'),
        'link': AppRoutes.PARTNERSCORE,
        'isLoading': isOverviewLoading,
        'data-testid': testIds.tabSwitcherLink('Partner Score'),
        'data-label-testid': testIds.tabSwitcherLabel('Partner Score'),
        'additionalContent': getChannelTabScore({
          id: PartnerScoreChannel.PARTNERSCORE,
          label: t('main.hcpOverview.overview.navigation.overview.label'),
          score: {
            label: t('main.hcpOverview.overview.navigation.overview.description'),
            value: overview?.currentAverage,
            valueSuffix: '/100',
            arrowValue: overview?.change || 0,
            reverseTitles: true,
          },
        }),
      },
      {
        'id': PartnerScoreChannel.CSATSCORE,
        'label': t('main.hcpOverview.overview.navigation.csat.label'),
        'link': AppRoutes.CSATSCORE,
        'isLoading': isCsatLoading,
        'data-testid': testIds.tabSwitcherLink('Csat Score'),
        'data-label-testid': testIds.tabSwitcherLabel('Csat Score'),
        'additionalContent': getChannelTabScore({
          id: PartnerScoreChannel.CSATSCORE,
          label: t('main.hcpOverview.overview.navigation.csat.label'),
          score: {
            label: t('main.hcpOverview.overview.navigation.csat.description'),
            value: csat?.currentAverage,
            valueSuffix: '%',
            arrowValue: csat?.change || 0,
            reverseTitles: true,
          },
        }),
      },
      {
        'id': PartnerScoreChannel.CESSCORE,
        'label': t('main.hcpOverview.overview.navigation.effortScore.label'),
        'link': AppRoutes.CESSCORE,
        'isLoading': isEffortScoreLoading,
        'data-testid': testIds.tabSwitcherLink('Effort Score'),
        'data-label-testid': testIds.tabSwitcherLabel('Effort Score'),
        'additionalContent': getChannelTabScore({
          id: PartnerScoreChannel.CESSCORE,
          label: t('main.hcpOverview.overview.navigation.effortScore.label'),
          score: {
            label: t('main.hcpOverview.overview.navigation.effortScore.description'),
            value: effortScore?.currentAverage,
            valueSuffix: '%',
            arrowValue: effortScore?.change || 0,
            reverseTitles: true,
          },
        }),
      },
      {
        'id': PartnerScoreChannel.NPSSCORE,
        'label': t('main.hcpOverview.overview.navigation.tnps.label'),
        'link': AppRoutes.NPSSCORE,
        'isLoading': isTnpsLoading,
        'data-testid': testIds.tabSwitcherLink('Net promoter score'),
        'data-label-testid': testIds.tabSwitcherLabel('Net promoter Score'),
        'additionalContent': getChannelTabScore({
          id: PartnerScoreChannel.NPSSCORE,
          label: t('main.hcpOverview.overview.navigation.tnps.label'),
          score: {
            label: t('main.hcpOverview.overview.navigation.tnps.description'),
            value: tnps?.percentageChangeString !== undefined ? parseFloat(tnps.percentageChangeString) : undefined,
            valueSuffix: ' / ± 100',
            arrowValue: tnps?.changeValue ? tnps?.changeValue * 100 : undefined,
            reverseTitles: true,
          },
        }),
      },
    ],
    [
      csat?.change,
      csat?.currentAverage,
      effortScore?.change,
      effortScore?.currentAverage,
      isCsatLoading,
      isEffortScoreLoading,
      isOverviewLoading,
      isTnpsLoading,
      overview?.change,
      overview?.currentAverage,
      t,
      tnps?.changeValue,
      tnps?.percentageChangeString,
    ],
  );

  const currentTabId = usePartnerScoreTabIdFromPathname();

  return (
    <>
      <NavigationTabList
        tabs={navigationTabList}
        adobeTrackingAnalytics={NAVIGATION_TAB_ADOBE_ANALYTICS_CONFIG}
        data-testid={testIds.tabSwitcherContainer}
      />
      <PartnerScoreContainer year={year} quarter={quarter} containerTitle={usePartnerScoreHeader(currentTabId)}>
        {children}
      </PartnerScoreContainer>
    </>
  );
}
